import cache from 'services/CacheService';
import lodashOmit from 'lodash.omit';
import {ordersService, statisticsService} from 'services';
import AbstractDataSource from 'store/modules/dashboards/dataGetter/dataSources/AbstractDataSource';
import {getPlanMetric} from 'components/Dashboards/WidgetSettings/utils';
import store from 'store/index';
import _ from 'lodash';

_.noConflict();

class OrderPeriodsAndStatDataSource extends AbstractDataSource {
  constructor ({dashboard, metrics, ...superArgs}) {
    super(superArgs);
    this.dashboard = dashboard;
    this.metrics = metrics;
    this.statisticsService = statisticsService;
    this.orderService = ordersService;
  }

  get cacheKey () {
    return `global-dashboards-orders-and-stat-${this.dashboard.id}`;
  }

  getPeriodsOrders (params, dashboardAccounts, dashboardProjects, withVat) {
    let projectId = null;
    if (store.getters['projects/currentProject']) {
      projectId = dashboardProjects[0];
    }
    const accountOrderPeriodsParams = {
      dateStart: params.dateStart,
      dateEnd: params.dateEnd,
      channelIds: params.channelIds,
      accountIds: dashboardAccounts,
      projectId,
      orderIds: params.filterEntities,
      withVat
    };

    return this.orderService.getAccountOrderPeriodsList(accountOrderPeriodsParams);
  }

  getStatistics (uniqParams, plannedForRequest, dashboardProjects, dashboardAccounts, metrics, withVat) {
    const fields = metrics.filter(x => Boolean(x));

    const filtering = uniqParams.filtering ? [...uniqParams.filtering] : [];
    let _entity = uniqParams.filteringType ? uniqParams.filteringType : uniqParams.grouping;
    if (_entity === 'order_periods') {
      _entity = 'order';
    }

    if (uniqParams.grouping && !_.isEmpty(uniqParams.filterEntities)) {
      filtering.push({
        entity: _entity,
        entities: uniqParams.filterEntities
      });
    }

    let projectId = null;
    if (store.getters['projects/currentProject']) {
      projectId = dashboardProjects[0];
    }

    // so as not to do backend for new filtering choice
    const _grouping = uniqParams.grouping === 'order_periods' ? 'no' : uniqParams.grouping;

    return this.statisticsService.getDetailedStatistics({
      accountIds: dashboardAccounts,
      channelIds: uniqParams.channelIds,
      dateEnd: uniqParams.dateEnd,
      dateStart: uniqParams.dateStart,
      dateGrouping: uniqParams.dateGrouping,
      grouping: _grouping,
      sorting: uniqParams.sorting,
      filtering,
      fields,
      projectId,
      profiles: uniqParams.profiles,
      goals: uniqParams.goals,
      withVat
    });
  }

  getPlannedMetrics (periodsData) {
    const totalPlannedMetrics = {};
    const plannedForRequest = [];
    const plannedForComponent = [];

    _.forEach(periodsData.total, (value, key) => {
      if (getPlanMetric(key) && !_.isNull(value) || key === 'budget') {

        // displayed metric is cost_fact, but planned value of cost_fact is in budget
        if (key === 'budget' && store.getters['user/canUser']('READ_COST_FACT')) {
          totalPlannedMetrics.cost_fact = value;
          plannedForRequest.push('cost_fact');
        } else {
          totalPlannedMetrics[getPlanMetric(key)] = value;
          plannedForRequest.push(getPlanMetric(key));
        }
      }
    });

    _.forEach(totalPlannedMetrics, (value, key) => {
      let metricData = {};
      metricData.metric = key;
      metricData.plan = value;
      metricData.fromPeriod = true;
      plannedForComponent.push(metricData);
    });

    return {
      plannedForRequest: plannedForRequest.filter(x => Boolean(x)),
      plannedForComponent: plannedForComponent.
        filter(m => this.metrics[m.metric]).
        map(m => {
          m.metricName = this.metrics[m.metric].name;
          m.metricType = this.metrics[m.metric].type;

          return m;
        })
    };
  }

  async makeRequest (uniqParams, metrics, dashboardAccounts, dashboardProjects, withVat) {
    let cancelRequest = true;
    let statAndPeriodsData;
    const emptyData = {
      items: [],
      items_planned: [],
      items_total: 0
    };

    if (!_.isEmpty(uniqParams.filteringProjects) && !_.isEmpty(uniqParams.filterEntities)) {
      cancelRequest = false;
    }

    // cancel request if there are no selected items in grouping or no projects in filtration
    if (cancelRequest) {
      return emptyData;
    }

    const periodsData = await this.getPeriodsOrders(uniqParams, dashboardAccounts, dashboardProjects, withVat);
    const {plannedForRequest, plannedForComponent} = this.getPlannedMetrics(periodsData);

    const statData = await this.getStatistics(
      uniqParams,
      plannedForRequest,
      dashboardProjects,
      dashboardAccounts,
      metrics,
      withVat
    );

    statAndPeriodsData = statData;
    statAndPeriodsData.items_planned = plannedForComponent;


    // if no data is available detailed statistics api method returns one row with zeroes
    if (statData.items.length && statData.items.length === 1) {
      const values = Object.values(statData.items[0]).filter(x => x && x !== '0');

      if (!values.length) {
        emptyData.items_planned = plannedForComponent;
        return emptyData;
      }
    }

    return statAndPeriodsData;
  }

  fetch (dsStatisticsParams, filtration, withVat, ignoreCache = false) {
    const metrics = dsStatisticsParams.metric;

    // remove metrics from stat request
    const fetchWidgetParams = lodashOmit(dsStatisticsParams, 'metric');

    return cache.asyncTryCacheOrCall({
      func: async ({metrics, ...uniqParams}) => this.makeRequest(
        uniqParams, metrics, this.dashboardParams.accountsFiltration, this.dashboardParams.projectsFiltration, withVat
      ),
      funcArgs: {metrics, filtration, ...fetchWidgetParams, withVat},
      onProjectLevel: false,
      key: this.cacheKey
    }, ignoreCache);
  }
}

export default OrderPeriodsAndStatDataSource;
